/** @jsx jsx */
import { jsx } from '@emotion/react';
import type { MouseEvent, ReactNode } from 'react';
import type { AnchorTarget, IconName } from '@zapier/design-system';
import {
  Animation,
  Colors,
  Icon,
  Link,
  TagLabel,
  Tooltip,
  Typography,
} from '@zapier/design-system';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import { useUniversalNavContext } from '../../hooks';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

export type Props = {
  /**
   * The URL to where the link goes.
   */
  href: string | null;
  /**
   * The `Icon` component to render within the link.
   * **Note that if renderBeforeText is defined, that prop is then responsible for rendering.**
   */
  icon?: IconName;
  /**
   * Indicates whether the link is collapsed down to just an icon.
   */
  isCollapsed?: boolean;
  /**
   * Indicates whether the link goes to a page outside of
   * the logged in app experience.
   */
  isExternal?: boolean;
  /**
   * Indicates whether the link is the selected/current link.
   */
  isSelected?: boolean;
  /**
   * This value is true iff this instance is in the UniversalSidebar
   */
  isSidebar?: boolean;
  /**
   * If true, adjusts layout for v3 sidebar links
   */
  isV3Link?: boolean;
  /**
   * Optional click handler for the link.
   */
  onClick?: React.MouseEventHandler;
  /**
   * Renders content before the link text. E.g; an Icon or Avatar
   */
  renderBeforeText?: (props: Props) => ReactNode;
  /**
   * The `role` attribute of the `UniversalNavLink`, which specifies
   * the function of the nested `Link`.
   */
  role?: string;
  /**
   * The text content to render within the link.
   */
  text: string;
  /**
   * Optional target for the link.
   **/
  target?: AnchorTarget;
  /**
   * Optional tag for the link. Typically "alpha" or "beta".
   **/
  tag?: string;
};

const tooltipClassName = createUniqueClassName('UniversalNavLink', 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;

const navIconClassName = createUniqueClassName('UniversalNavLink', 'navIcon');
const navIconSelector = `.${navIconClassName}`;

const externalIconClassName = createUniqueClassName(
  'UniversalNavLink',
  'externalIcon'
);
const externalIconSelector = `.${externalIconClassName}`;

const Styles = labelStyles('UniversalNavLink', {
  root: (
    props: Pick<Props, 'isSidebar' | 'isCollapsed' | 'isSelected'>,
    hasBeforeText: boolean
  ) => [
    // Rare specificity bump over `Link`.
    withModifier('', {
      ...Typography.paragraph3Medium,
      color: Colors.neutral800,
      display: 'flex',
      position: 'relative',
      minHeight: 44,
      padding: 10,
      borderRadius: 3,
      whiteSpace: 'nowrap',
      width: '100%',
      transition: `${Animation.transitionValue}, font-weight 0s, width 0s`,
      textDecoration: 'none',

      '&:hover, &:focus': {
        transition: 'none',
        color: Colors.neutral800,
        backgroundColor: Colors.neutral200,
        outline: 'none',
      },
      [navIconSelector]: {
        color: Colors.neutral600,
      },
      [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
        transition: 'none',
        color: Colors.neutral800,
      },
      ...(props.isSidebar && {
        ...Typography.paragraph3Medium,
        borderRadius: 15,
        color: Colors.neutral700,
        outline: 'solid 2px transparent',
        '&:hover, &:focus': {
          transition: 'none',
          backgroundColor: Colors.cream,
          outline: `solid 2px transparent`,
        },
        '&:focus': {
          ...Typography.paragraph3Bold,
          outlineOffset: '-1px',
          outline: `solid 2px ${Colors.brand}`,
        },
        [navIconSelector]: {
          color: Colors.neutral800,
        },
        [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
          transition: 'none',
        },
      }),
    }),
    !hasBeforeText &&
      withModifier('textOnly', {
        paddingLeft: 15,
        paddingRight: 15,
      }),
    props.isSelected &&
      withModifier('selected', {
        ...Typography.paragraph3Bold,
        color: Colors.blue,
        backgroundColor: Colors.blue10,
        [navIconSelector]: {
          color: Colors.blue,
        },
        ...(props.isSidebar && {
          color: Colors.neutral800,
          backgroundColor: Colors.cream,
          [navIconSelector]: { color: Colors.brand },
        }),
      }),
    props.isCollapsed === false && {},
    props.isCollapsed &&
      withModifier('collapsed', {
        width: 50,
        left: 2,
      }),
    {
      [tooltipSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.1s ease-in-out',
        '@media (hover: none)': {
          display: 'none',
        },
      },
      [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    {
      [externalIconSelector]: {
        opacity: 0,
      },
      [`&:hover ${externalIconSelector}, &:focus ${externalIconSelector}`]: {
        opacity: 0.5,
      },
    },
  ],
  layout: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: 20,
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  v3Layout: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
    paddingLeft: 3,
  },
  text: {
    '&[aria-hidden="true"]': {
      opacity: 0,
    },
  },
});

/**
 * @private
 * Renders a link within the universal sidebar.
 */
const UniversalNavLink = ({
  href,
  text,
  icon = undefined,
  isCollapsed = false,
  isExternal = false,
  isSelected = false,
  isSidebar = undefined,
  isV3Link = false,
  onClick: onClickProp = undefined,
  role = undefined,
  tag = undefined,
  target = undefined,
  renderBeforeText = (props: Props) =>
    props.icon && (
      <span className={navIconClassName}>
        <Icon isBlock={true} name={props.icon} size={isV3Link ? 24 : 30} />
      </span>
    ),
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const props = {
    href,
    text,
    icon,
    isCollapsed,
    isExternal,
    isSelected,
    isSidebar,
    onClick: onClickProp,
    role,
    target,
    renderBeforeText,
  } satisfies Props;

  const tooltip = isCollapsed && (
    <span className={tooltipClassName}>
      <Tooltip position="east">{text}</Tooltip>
    </span>
  );
  const onClick = (e: MouseEvent) => {
    // Chrome is the only browser that causes an element to
    // be in a `:focus` state when it's clicked. That `:focus`
    // state causes `Tooltip` to undesirably render. The user
    // already clicked, so they don't need to see the tooltip.
    // To hide the tooltip, `blur` the node to cause it to no
    // longer have `:focus`. Tabbing location is still preserved.
    (e.currentTarget as HTMLElement).blur();
    if (onClickProp) {
      onClickProp(e);
    }
  };
  const beforeText = renderBeforeText && renderBeforeText(props);

  return (
    <Link
      color={null}
      css={Styles.root({ isSidebar, isCollapsed, isSelected }, !!beforeText)}
      href={getFullyQualifiedURL(href)}
      onClick={onClick}
      role={role}
      target={target || '_self'}
    >
      <span css={isV3Link ? Styles.v3Layout : Styles.layout} data-zds>
        {beforeText}
        <span aria-hidden={isCollapsed} css={Styles.text} data-zds>
          {text}
        </span>

        {tag && (
          <TagLabel size="small" color="lavender">
            {tag}
          </TagLabel>
        )}

        {isExternal && !isCollapsed && (
          <span className={externalIconClassName} data-zds>
            <Icon isBlock={true} name="arrowOffsite" size={18} />
          </span>
        )}
      </span>
      {tooltip}
    </Link>
  );
};

export default UniversalNavLink;
